import ManagedTable, { ManagedColumn, ManagedColumnExtra, ManagedTableProps } from 'app/common/table/ManagedTable';
import { MeasureDto, RegulationDto, RegulationProposalAction } from 'app/model/CORE';
import TimeUtils from 'app/common/util/TimeUtils';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { CheckCircleFilled, ForwardOutlined, StarFilled } from '@ant-design/icons';
import useWindowDimensions from 'app/common/hooks/useWindowDimensions';
import React from 'react';
import { Flex } from 'app/components/common/Flex';
import ConditionalRender from 'app/common/components/ConditionalRender';
import 'app/components/table/McdmMeasureTable.less';
import { MonitoredMcdmMeasure } from 'app/model/SOL49';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import { ModalHandler } from 'app/components/panels/ModalHandler';
import Utils from '../../common/util/Utils';
import { OpenMcdmMeasure, PartialDashboardDataUpdateEvent } from '../../common/eventbus/Events';
import { EntityType } from '../../model/client/SOL49';
import { StandardColumnTooltips } from 'app/components/table/StandardColumnTooltips';

class McdmMeasureColumn extends ManagedColumn<MonitoredMcdmMeasure> {
    constructor(
        header: string,
        width: string,
        getCellValue: (object: MonitoredMcdmMeasure) => any,
        extra?: ManagedColumnExtra<MonitoredMcdmMeasure>,
    ) {
        super(header, width, getCellValue, extra);
    }
}

export const MCDM_MEASURE_SELECTION_COLUMN = new McdmMeasureColumn(
    '',
    '2.5em',
    () => (
        <div className={'check-circle'}>
            <CheckCircleFilled />
        </div>
    ),
    { name: 'selected' },
);

const renderStatus = (mcdmMeasure: MonitoredMcdmMeasure, onClose: () => void) => {
    const remark = mcdmMeasure.mcdmMeasureTopic.remark;
    const proposalNote = mcdmMeasure.mcdmMeasureTopic.proposalNote;
    let userId = remark ? remark.match(/USER\((.*)\)/) : null;
    // check proposal note for userId
    if (!userId && proposalNote) {
        userId = proposalNote.match(/USER\((.*)\)/);
    }
    const alternative = proposalNote ? proposalNote.match(/ALT\((.*)\)/) : null;
    const amended = proposalNote && proposalNote.includes('AMENDED');
    const isActive = MeasureUtils.isMcdmStateActive(mcdmMeasure.mcdmMeasureTopic.state);

    return (
        <Flex
            direction="vertical"
            style={{ alignItems: 'flex-start' }}
            onClick={e => {
                e.stopPropagation();
                if (isActive) {
                    if (AuthorisationUtils.isNmUser()) {
                        MeasureUtils.acknowledgeMcdmMeasures([mcdmMeasure]).then(([mcdmMeasure]) => {
                            // Close McdmPanel, open solver and refresh mcdmMeasures
                            onClose();
                            OpenMcdmMeasure.fireEvent([mcdmMeasure]);
                            PartialDashboardDataUpdateEvent.fireEvent(EntityType.MEASURE_PROPOSAL);
                        });
                    } else {
                        // Close McdmPanel && Open reg editor
                        onClose();
                        ModalHandler.showRegulationEditor(
                            MeasureUtils.getTvLocation(mcdmMeasure.measure).id,
                            'tv',
                            mcdmMeasure.measure,
                        );
                    }
                }
            }}
            className={`status ${isActive ? '' : 'inactive'}`}>
            <Flex className="state">
                <span>{MeasureUtils.toMcdmMeasureStateString(mcdmMeasure.mcdmMeasureTopic)}</span>
                <ForwardOutlined />
            </Flex>
            <ConditionalRender condition={Utils.size(userId) > 1}>
                <span>User: {userId?.[1]}</span>
            </ConditionalRender>
            <ConditionalRender condition={Utils.size(alternative) > 1}>
                <span>Alt: {alternative?.[1]}</span>
            </ConditionalRender>
            <ConditionalRender condition={amended}>
                <span>AMENDED</span>
            </ConditionalRender>
        </Flex>
    );
};

const renderType = (mcdmMeasure: MonitoredMcdmMeasure) => {
    return (
        <Flex direction="horizontal" style={{ alignItems: 'center', justifyContent: 'center' }}>
            <ConditionalRender condition={MeasureUtils.isNewMcdmMeasure(mcdmMeasure)}>
                <StarFilled className={'star'} />
            </ConditionalRender>
            {MeasureUtils.isRegulationProposal(mcdmMeasure.measure) ? 'REGULATION' : 'REROUTING'}
        </Flex>
    );
};

const renderProposalApplicability = (
    measure: MeasureDto,
    regulation: RegulationDto,
    field: string,
    equalFn: (a, b) => boolean,
) => {
    if (MeasureUtils.isRegulationProposal(measure)) {
        const fieldUpdated =
            measure.action === RegulationProposalAction.UPDATE &&
            !equalFn(regulation.applicability[field], measure.applicability[field]);

        return (
            <Flex direction="vertical">
                <span className={fieldUpdated ? 'highlight' : ''}>
                    {TimeUtils.formatHHMM(measure.applicability[field])}
                </span>
                <ConditionalRender condition={fieldUpdated}>
                    <i>
                        <s>{TimeUtils.formatHHMM(regulation?.applicability[field])}</s>
                    </i>
                </ConditionalRender>
            </Flex>
        );
    } else {
        return (
            <Flex direction="vertical">
                <span>{TimeUtils.formatHHMM(measure.applicability[field])}</span>
            </Flex>
        );
    }
};

const renderTFV = (measure: MeasureDto) => {
    return (
        <Flex direction="vertical" style={{ alignItems: 'flex-start' }}>
            <span>{MeasureUtils.getTvLocation(measure).id}</span>
            <ConditionalRender condition={MeasureUtils.isRerouting(measure)}>
                <span>{measure.scenarioReference?.scenarioId}</span>
            </ConditionalRender>
        </Flex>
    );
};

const renderCapacity = (mcdmMeasure: MonitoredMcdmMeasure) => {
    return (
        <span>
            {MeasureUtils.toHourlyCapacityString(mcdmMeasure.hourlyCapacity, mcdmMeasure.measure.applicability)}
        </span>
    );
};

const renderRate = (mcdmMeasure: MonitoredMcdmMeasure) => {
    const { measure, linkedRegulation } = mcdmMeasure;

    let constraints = <span>---</span>; // Default value for Rerouting

    if (MeasureUtils.isRegulationProposal(measure)) {
        const proposedConstraint = MeasureUtils.toRegulationRateString(measure);
        const isUpdate = measure.action === RegulationProposalAction.UPDATE;
        const previousConstraints = isUpdate && MeasureUtils.toRegulationRateString(linkedRegulation);
        constraints = (
            <>
                <span className={isUpdate && proposedConstraint !== previousConstraints ? 'highlight' : ''}>
                    {proposedConstraint}
                </span>
                <ConditionalRender condition={isUpdate && proposedConstraint !== previousConstraints}>
                    <i>
                        <s>
                            <span>{previousConstraints}</span>
                        </s>
                    </i>
                </ConditionalRender>
            </>
        );
    }

    return <Flex direction="vertical">{constraints}</Flex>;
};

const renderReason = (reason: string) => (
    <Flex direction="vertical" style={{ alignItems: 'flex-start' }}>
        <span>{reason}</span>
    </Flex>
);

export class McdmMeasureColumns {
    public static readonly TYPE = new McdmMeasureColumn('Type', '11em', renderType);
    public static readonly REGULATION_EOC = new McdmMeasureColumn(
        'E/O/C',
        '5em',
        object => (object.measure.cherryPicking ? 'C' : MeasureUtils.isEntryRegulation(object.measure) ? 'E' : 'O'),
        {
            tooltip: StandardColumnTooltips.REGULATION_EOC,
        },
    );
    public static readonly ID = new McdmMeasureColumn('ID', '8em', object => object.measure.measureId.id);
    public static readonly TV = new McdmMeasureColumn('TFV', '9em', object => object.measure, {
        formatter: renderTFV,
        comparator: (measure1, measure2) =>
            MeasureUtils.getTvLocation(measure1).id.localeCompare(MeasureUtils.getTvLocation(measure2).id),
    });
    public static readonly START_TIME = new McdmMeasureColumn('Start Time', '7em', object =>
        renderProposalApplicability(object.measure, object.linkedRegulation, 'from', TimeUtils.isEquals),
    );
    public static readonly END_TIME = new McdmMeasureColumn('End Time', '7em', object =>
        renderProposalApplicability(object.measure, object.linkedRegulation, 'to', TimeUtils.isEquals),
    );
    public static readonly CAPACITY = new McdmMeasureColumn('MV', '4em', renderCapacity);
    public static readonly CONSTRAINTS = new McdmMeasureColumn('Rate', '4em', renderRate, { wordWrapEnabled: true });
    public static readonly REASON = new McdmMeasureColumn(
        'Reason',
        '13em',
        mcdmMeasure => MeasureUtils.toMeasureReasonString(mcdmMeasure.measure),
        {
            formatter: (reason: string) => renderReason(reason),
        },
    );
    public static readonly ACTION = new McdmMeasureColumn('Action', '6em', object =>
        MeasureUtils.isRegulationProposal(object.measure)
            ? MeasureUtils.toActionShortString(object.measure.action)
            : 'CREATE',
    );
    public static STATUS = (onClose?: () => void): McdmMeasureColumn =>
        new McdmMeasureColumn('Status', '17em', object => [object, onClose], {
            wordWrapEnabled: true,
            formatter: ([object, onClose]) => renderStatus(object, onClose),
            comparator: ([monitoredMeasure1], [monitoredMeasure2]) =>
                MeasureUtils.toMcdmMeasureStateString(monitoredMeasure1.mcdmMeasureTopic).localeCompare(
                    MeasureUtils.toMcdmMeasureStateString(monitoredMeasure2.mcdmMeasureTopic),
                ),
        });
}

class McdmMeasureTableBase extends ManagedTable<MonitoredMcdmMeasure> {}

const McdmMeasureTable = (props: ManagedTableProps<MonitoredMcdmMeasure>) => {
    const { width } = useWindowDimensions();
    const fontSize = (0.45 * width) / 100;

    return <McdmMeasureTableBase fontSize={fontSize} {...props} />;
};

export default McdmMeasureTable;
